<script setup lang="ts">
import { ref } from 'vue'
import { personalInfo } from '../../data/personal'
import ContactForm from '../contact/ContactForm.vue'
import SocialLinks from '../contact/SocialLinks.vue'
import { revealDirective } from '../../composables/useReveal'

defineOptions({ directives: { reveal: revealDirective } })

const copied = ref(false)

async function copyEmail() {
  try {
    await navigator.clipboard.writeText(personalInfo.contact.email)
    copied.value = true
    setTimeout(() => (copied.value = false), 1800)
  } catch {
    /* clipboard blocked — the mailto link below still works */
  }
}
</script>

<template>
  <section id="contact" class="section cv-auto">
    <div class="wrap">
      <div class="section-head" v-reveal>
        <h2 class="display display-3">Contact</h2>
        <p class="eyebrow">{{ personalInfo.contact.availability }}</p>
      </div>

      <div class="layout">
        <!-- ---------- invitation ---------- -->
        <div class="pitch" v-reveal>
          <p class="standfirst">
            If you're building something in health, tooling, or anything that has
            to work on a bad connection — I'd like to hear about it.
          </p>

          <div class="direct">
            <a class="email display display-3" :href="`mailto:${personalInfo.contact.email}`">
              {{ personalInfo.contact.email }}
            </a>
            <button class="copy meta" @click="copyEmail">
              {{ copied ? 'Copied' : 'Copy' }}
            </button>
          </div>

          <dl class="expect">
            <div>
              <dt class="eyebrow">Reply time</dt>
              <dd>A day or two</dd>
            </div>
            <div>
              <dt class="eyebrow">Based</dt>
              <dd>{{ personalInfo.contact.location }}</dd>
            </div>
          </dl>

          <SocialLinks class="socials" />
        </div>

        <!-- ---------- form ---------- -->
        <div v-reveal="{ staggerInterval: 120 }">
          <ContactForm />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.section-head {
  margin-bottom: clamp(2rem, 5vw, 3.5rem);
}

.layout {
  display: grid;
  gap: clamp(2.5rem, 6vw, 5rem);
  grid-template-columns: 1fr;
  align-items: start;
}
@media (min-width: 940px) {
  .layout {
    /* form capped so inputs don't stretch to comedic widths in the wide wrap */
    grid-template-columns: minmax(0, 1.1fr) minmax(20rem, 36rem);
  }
}

.standfirst {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: var(--display-3);
  line-height: 1.24;
  letter-spacing: -0.025em;
  color: var(--c-ink);
  margin: 0 0 2rem;
  max-width: 30ch;
  text-wrap: balance;
}

.direct {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 1.4rem;
  border-bottom: 1px solid var(--c-rule);
}
.email {
  color: var(--c-ink);
  word-break: break-all;
  transition: color 0.2s ease;
}
.email:hover {
  color: var(--c-accent);
}
.copy {
  flex: none;
  background: none;
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  padding: 0.2rem 0.6rem;
  color: var(--c-ink-2);
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
}
.copy:hover {
  border-color: var(--c-accent);
  color: var(--c-accent);
}

.expect {
  display: flex;
  gap: 2.5rem;
  flex-wrap: wrap;
  margin: 1.4rem 0 2.2rem;
}
.expect dt {
  margin-bottom: 0.3rem;
}
.expect dd {
  margin: 0;
  color: var(--c-ink);
  font-size: 0.95rem;
}

.socials {
  max-width: 22rem;
}
</style>
